import { productCardCarousels } from '~js/utils/product-card-swiper';
import { postAddToCart } from '../../utils/variant';
import { QuickAddProductDrawer } from '../snippets/card-drawer';

class ProductDrawer {
  constructor(section, card) {
    this.section = section;
    this.optionInputs = null;
    this.optionGroups = null;
    this.variantSelectElement = null;
    this.productBuyBox = null;
    this.oneTimeOption = null;
    this.selectedOptionsString = null;
    this.optionsDivider = ' / ';
    this.addToCartButton = null;
    this.card = card;

    this.initializeElements();
    this.initializeColorSwatchCarousels();
    this.addEventHandlers();
    this.updateSwatchAvailability();
    this.updateSubscriptionButtonToChecked();
  }

  // Renders the product drawer content
  static render(section, productHTML, card) {
    if (!productHTML) {
      section.innerHTML = 'Product details are unavailable, please try again.';
      return null;
    }
    section.innerHTML = productHTML;
    return new ProductDrawer(section, card);
  }

  initializeElements() {
    this.optionInputs = this.section.querySelectorAll(
      '.js-quick-add-product-option-group-value',
    );
    this.optionGroups = this.section.querySelectorAll(
      '.js-quick-add-product-options-group',
    );
    this.variantSelectElement = this.section.querySelector(
      '.js-quick-add-product-variant-select-element',
    );
    this.productBuyBox = this.section.querySelector(
      '.js-card-drawer-product-buybox',
    );
    this.oneTimeOption = this.productBuyBox?.querySelector(
      'input[value="one_time"]',
    );
    this.addToCartButton = this.section.querySelector(
      '.js-product-quick-buy-add-to-bag',
    );
    this.selectedOptionsString =
      this.variantSelectElement?.querySelector(
        'option:checked',
      )?.dataset?.title;
  }

  initializeColorSwatchCarousels() {
    const colorSwatchContainer = this.section.querySelector(
      '.js-color-swatch-swiper-container',
    );
    this.productColorSwiper = productCardCarousels(
      colorSwatchContainer,
      10,
      10,
      8,
      this.section.querySelector(
        '.js-product-drawer-color-swatch-thumbnail-next',
      ),
      this.section.querySelector(
        '.js-product-drawer-color-swatch-thumbnail-prev',
      ),
    );
  }

  addEventHandlers() {
    this.optionInputs.forEach((option) => {
      option.addEventListener('change', () => {
        this.handleVariantSelection();
        this.updateSwatchAvailability();
        this.updateVariantPrices();
      });
    });

    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }
  }

  updateVariantPrices() {
    const price = this.variantSelectElement
      ?.querySelector('option:checked')
      ?.getAttribute('price');
    const compareAtPrice = this.variantSelectElement
      ?.querySelector('option:checked')
      ?.getAttribute('compare_at_price');

    const priceElement = this.section.querySelectorAll(
      '.js-quick-add-product-variant-price',
    );
    const compareAtPriceElement = this.section.querySelectorAll(
      '.js-quick-add-product-variant-compare_at_price',
    );
    compareAtPriceElement.forEach((element) => {
      element.innerHTML = compareAtPrice;
    });
    priceElement.forEach((element) => {
      element.innerHTML = price;
    });
  }

  updateSwatchAvailability() {
    this.optionGroups.forEach((group, groupIndex) => {
      const options = group.querySelectorAll(
        '.js-quick-add-product-option-group-value',
      );
      options.forEach((input) => {
        const selectedOptions = this.selectedOptionsString.split(
          this.optionsDivider,
        );
        selectedOptions[groupIndex] = input.value;
        const variantSelector = selectedOptions.join(this.optionsDivider);
        const variantSelectOption = this.variantSelectElement.querySelector(
          `[data-title="${variantSelector}"]`,
        );
        input.toggleAttribute('disabled', variantSelectOption.disabled);
      });
    });
  }

  handleVariantSelection() {
    const selectedOptions = [];
    this.optionGroups.forEach((optionGroup) => {
      const selectedOption = optionGroup.querySelector('input:checked');
      selectedOptions.push(selectedOption.value);
      this.updateSelectedOptionLabel(optionGroup, selectedOption.value);
    });
    const selectedOptionsString = selectedOptions.join(this.optionsDivider);
    this.selectedOptionsString = selectedOptionsString;
    const matchingOption = this.variantSelectElement.querySelector(
      `[data-title="${selectedOptionsString}"]`,
    );
    this.variantSelectElement.value = matchingOption.value;
    this.variantSelectElement.dispatchEvent(new Event('change'));

    if (this.productBuyBox) this.updateVariantSellingPlan(matchingOption);
  }

  updateVariantSellingPlan(variant) {
    const hasSellingPlans =
      variant.dataset?.hasSellingPlans === 'true' ? true : false;

    // Toggle Buy Box based on variant selling plan
    this.productBuyBox.classList.toggle('hidden', !hasSellingPlans);
    if (!hasSellingPlans && this.oneTimeOption && !this.oneTimeOption.checked) {
      this.oneTimeOption.click();
    }
  }

  updateSelectedOptionLabel(group, value) {
    group.querySelector('.js-quick-add-product-option-value').innerHTML = value;
  }

  toggleAddToCartButtonState(isAddingProduct) {
    this.addToCartButton.classList.toggle('is-adding', isAddingProduct);
    const quickBuyText = this.addToCartButton.querySelector(
      '.quick-buy-cta-text',
    );
    const quickBuyLoader = this.addToCartButton.querySelector(
      '.quick-buy-cta-loader',
    );
    if (quickBuyText) {
      quickBuyText.classList.toggle('hidden', isAddingProduct);
    }

    if (quickBuyLoader) {
      quickBuyLoader.classList.toggle('hidden', !isAddingProduct);
    }
  }

  updateSubscriptionButtonToChecked() {
    const frequencySelects = this.section.querySelectorAll(
      'select[name="frequency"]',
    );
    frequencySelects.forEach((selectElement, index) => {
      selectElement.addEventListener('change', () => {
        const subscriptionRadio = this.section.querySelector(
          `.subscription-radio-${index + 1}`,
        );
        subscriptionRadio.checked = true;
      });
    });
  }

  async addToCart() {
    const variantId = this.variantSelectElement.value;
    if (!variantId) return;
    this.toggleAddToCartButtonState(true);
    let selectedFrequency = null;
    const selectedOptionElement = this.section.querySelector(
      'input.js-purchase-option:checked',
    );
    if (selectedOptionElement) {
      const selectedOptionValue = selectedOptionElement.value;
      if (selectedOptionValue !== 'one_time') {
        const closestOption = selectedOptionElement.closest(
          '.js-subscription-options__option',
        );
        selectedFrequency = closestOption.querySelector(
          'select[name="frequency"]',
        ).value;
      }
    }
    const response = await postAddToCart(variantId, selectedFrequency);
    if (!response) return;
    this.toggleAddToCartButtonState(false);
    QuickAddProductDrawer.hide(this.card);
  }
}

export const renderProductDrawerModal = (section, productHTML, card) => {
  return ProductDrawer.render(section, productHTML, card);
};
